import Request from "./Request";

// force frontend rebuild
const FlightService = {

    async getFlightJobs() {
        return await Request.get('/api/flight-jobs')
    },

    async createFlightJob(data) {
        return await Request.post('/api/flight-jobs', data)
    },

    async updateFlightJob(flight_job_id, data) {
        console.log('data', data)
        return await Request.post(`/api/flight-jobs/${flight_job_id}`, data)
    },

    async deleteFlightJob(flight_job_id) {
        return await Request._delete(`/api/flight-jobs/${flight_job_id}`)
    },

    async activateFlightJob(flight_job_id) {
        return await Request.post(`/api/flight-jobs/${flight_job_id}/activate`)
    },

    async runFlightJob(flight_job_id, data) {
        return await Request.post(`/api/flight-jobs/${flight_job_id}/run`, data)
    },

    async executeFlightJob(flight_job_log_id) {
        const key = 'scHzcX1L4ZFjj5exMg57dB1aq1HtF0';
        return await Request.post(`/api/flight-jobs/${flight_job_log_id}/execute?key=${key}`);
    },

    async executeDailyLightSpeedJob(flight_job_log_id) {
        const key = 'scHzcX1L4ZFjj5exMg57dB1aq1HtF0';
        return await Request.post(`/api/flight-jobs/execute/lightspeed/${flight_job_log_id}?key=${key}`);
    },
    async uploadDailyLightspeedJob() {
        const key = 'scHzcX1L4ZFjj5exMg57dB1aq1HtF0';
        return await Request.post(`/api/flight-jobs/execute/lightspeed?key=${key}`);
    },

    async deactivateFlightJob(flight_job_id) {
        return await Request.post(`/api/flight-jobs/${flight_job_id}/deactivate`)
    },

    async getFlightJobLogs() {
        return await Request.get('/api/flight-job-logs')
    },

    /**
     * Get recent flight job logs.
     *
     * @returns {Promise}
     */
    async getRecentFlightJobLogs() {
        return await Request.get('/api/flight-job-logs/recent')
    },

    /**
     * Get flight job logs for client
     *
     * @returns {Promise}
     */
    async getFlightJobLogsForClient(clientId) {
        return await Request.get(`/api/clients/${clientId}/flight-job-logs`)
    },

    /**
     * Get flight job log by id
     *
     * @param {Number} flightJobLogId
     * @param {Array} relationsToLoad
     *
     * @returns {Promise}
     */
    async getFlightJobLogById({ flightJobLogId, relationsToLoad = [] }) {
        return await Request.get(`/api/flight-job-log/${flightJobLogId}`, { with: relationsToLoad.join(',') })
    },

    /**
     * Get flight job settings for client
     * @returns {Promise}
     */
    async getFlightJobSettingsForClient(clientId) {
        return await Request.get(`/api/clients/${clientId}/flight-job`)
    },

    async broadcastJob() {
        const key = 'scHzcX1L4ZFjj5exMg57dB1aq1HtF0';
        return await Request.post(`/api/flight-jobs/broadcast?key=${key}`)
    },

    /**
     * Get all flight clients.
     *
     * @returns {Promise}
     */
    async getFlightClients() {
        return await Request.get('/api/flight-clients')
    },

    /**
     * Sync data from staging to actuals
     *
     * @returns {Promise}
     */
    async syncFromStagingToActuals(clientId) {
        return await Request.get(`/api/clients/${clientId}/flight/sync-from-staging`)
    },

    async mergeHeartlandActuals(clientId, startMonth, endMonth) {
        return await Request.post(`/api/clients/${clientId}/heartland/merge-actuals`, { startMonth, endMonth })
    },

    /**
     * Re-run failed LS Daily Jobs
     *
     * @returns {Promise}
     */
    async reRunFailedLSDailyJobs() {
        return await Request.get('/api/flight-jobs/re-run-failed-ls-daily-jobs')
    },

    /**
     * Load history for flight job
     *
     * @param {String} clientId
     * @param {Number} monthsBack
     * @param {{ skipInventory?: boolean }} options
     *
     * @returns {Promise}
     */
    async loadHistory(clientId, monthsBack, options = {}) {
        const params = { 'months-back': monthsBack };
        if (typeof options.skipInventory !== 'undefined') {
            params['skipInventory'] = options.skipInventory;
        }
        return Request.get(`/api/clients/${clientId}/flight/load-history`, params, true)
    },

    /**
     * Migrate Shopify Client to Real Client
     *
     * @param {String} clientId
     * @param {String} targetClientId
     * @returns {Promise}
     */
    async migrateShopifyClientToRealClient(clientId, targetClientId) {
        return Request.post(`/api/clients/${clientId}/shopify-migrate-to/${targetClientId}`)
    },

    /**
     * Get Shopify location overrides for client
     *
     * @param {String|Number} clientId
     * @returns {Promise}
     */
    async getShopifyLocationOverrides(clientId) {
        return Request.get(`/api/clients/${clientId}/shopify-location-overrides`);
    },

    /**
     * Save Shopify location overrides for client
     *
     * @param {String|Number} clientId
     * @param {Record<string, (number|null|string)>} overrides Map of SALES_CHANNEL => OVERRIDE_LOCATION_ID
     * @returns {Promise}
     */
    async saveShopifyLocationOverrides(clientId, overrides) {
        return Request.post(`/api/clients/${clientId}/shopify-location-overrides`, { overrides });
    },

    async getLightspeedXSeriesSalesStatuses(clientId) {
        return Request.get(`/api/clients/${clientId}/lightspeed-xseries/sales-statuses`);
    },

    async saveLightspeedXSeriesSalesStatusSelection(clientId, statuses) {
        return Request.post(`/api/clients/${clientId}/lightspeed-xseries/sales-statuses/selected`, { statuses });
    },

    /**
     * Get the Lightspeed X-Series sales statuses for a client.
     * Returns the available status options (known + observed in BQ) and
     * the currently selected statuses persisted in client_extra.
     *
     * @param {String|Number} clientId
     * @returns {Promise<{client_id: number, available: string[], observed_in_bq: string[], selected: string[]}>}
     */
    async getLightspeedXSeriesSalesStatuses(clientId) {
        return Request.get(`/api/clients/${clientId}/lightspeed-xseries/sales-statuses`);
    },

    /**
     * Persist the support-selected Lightspeed X-Series sales statuses
     * for a client.
     *
     * @param {String|Number} clientId
     * @param {string[]} statuses
     * @returns {Promise}
     */
    async saveLightspeedXSeriesSalesStatusSelection(clientId, statuses) {
        return Request.post(`/api/clients/${clientId}/lightspeed-xseries/sales-statuses/selected`, { statuses });
    },

    /**
     * Copy locations, departments, classes, and location classes
     * from a source client onto the current client.
     *
     * Fails if any classes already exist on the destination client.
     *
     * @param {String|Number} destClientId Current (destination) client id
     * @param {String|Number} sourceClientId Source client id
     * @returns {Promise}
     */
    async copyClientStructureFromSource(destClientId, sourceClientId) {
        return Request.post(`/api/clients/${destClientId}/shopify/copy-structure-from/${sourceClientId}`);
    },

    /**
     * Merge Shopify raw data into actuals for a client over a date range.
     *
     * @param {String|Number} clientId
     * @param {String} startDate YYYY-MM-DD (first of month)
     * @param {String} endDate YYYY-MM-DD (first of month)
     * @param {Boolean} includeHardMarkdowns
     * @returns {Promise}
     */
    async mergeShopifyActuals(clientId, startDate, endDate, includeHardMarkdowns) {
        return Request.post(`/api/clients/${clientId}/shopify/merge-actuals`, {
            startDate,
            endDate,
            includeHardMarkdowns: includeHardMarkdowns ? 1 : 0,
        });
    },

    /**
     * Merge Shopify raw data into actuals for historical months only
     * (strictly before the current calendar month).
     * Uses INVENTORY_BY_LOCATION_MONTHLY (EOM snapshots).
     *
     * @param {String|Number} clientId
     * @param {String} startDate YYYY-MM-DD (first of month)
     * @param {String} endDate YYYY-MM-DD (must be before current month)
     * @param {Boolean} includeHardMarkdowns
     * @returns {Promise}
     */
    async mergeShopifyActualsHistory(clientId, startDate, endDate, includeHardMarkdowns) {
        return Request.post(`/api/clients/${clientId}/shopify/merge-actuals-history`, {
            startDate,
            endDate,
            includeHardMarkdowns: includeHardMarkdowns ? 1 : 0,
        });
    },

    /**
     * Merge Shopify raw data into actuals for the current calendar month only.
     * Uses INVENTORY_BY_LOCATION_CURRENT (live snapshot from nightly flight).
     *
     * @param {String|Number} clientId
     * @param {Boolean} includeHardMarkdowns
     * @returns {Promise}
     */
    async mergeShopifyActualsCurrentMonth(clientId, includeHardMarkdowns) {
        return Request.post(`/api/clients/${clientId}/shopify/merge-actuals-current-month`, {
            includeHardMarkdowns: includeHardMarkdowns ? 1 : 0,
        });
    },

    /**
     * Merge purchase-order on-order data into actuals for the current client.
     * Supports Shopify, Stocky, and Faves purchase-order systems.
     *
     * @param {String|Number} clientId
     * @param {String|null} purchaseOrderSystem shopify|stocky|faves (optional, defaults to client setting)
     * @param {String|null} onOrderMode current|last_month (optional)
     * @returns {Promise}
     */
    async mergePurchaseOrderOnOrder(clientId, purchaseOrderSystem = null, onOrderMode = null) {
        const body = {};
        if (purchaseOrderSystem) body.purchaseOrderSystem = purchaseOrderSystem;
        if (onOrderMode) body.onOrderMode = onOrderMode;
        return Request.post(`/api/clients/${clientId}/shopify/merge-purchase-orders-on-order`, body);
    },

    /**
     * Merge purchase-order receiving data into actuals for the current client.
     * Supports Shopify, Stocky, and Faves purchase-order systems.
     *
     * @param {String|Number} clientId
     * @param {String|null} purchaseOrderSystem shopify|stocky|faves (optional, defaults to client setting)
     * @param {String|null} startDate YYYY-MM-DD (first of month, optional)
     * @param {String|null} endDate YYYY-MM-DD (first of month, optional)
     * @returns {Promise}
     */
    async mergePurchaseOrderReceiving(clientId, purchaseOrderSystem = null, startDate = null, endDate = null) {
        const body = {};
        if (purchaseOrderSystem) body.purchaseOrderSystem = purchaseOrderSystem;
        if (startDate) body.startDate = startDate;
        if (endDate) body.endDate = endDate;
        return Request.post(`/api/clients/${clientId}/shopify/merge-purchase-orders-receiving`, body);
    },

    /**
     * Run a Shopify Pilot advanced manual flight with only the specified steps.
     *
     * @param {String|Number} clientId
     * @param {String} period - yyyymm format (e.g. "202604")
     * @param {Array<string>} steps - subset of: products, inventory, orders, transfers, purchase_orders
     * @returns {Promise}
     */
    async runShopifyPilotAdvanced(clientId, period, steps, mergeOptions = {}) {
        return Request.post(`/api/clients/${clientId}/shopify-pilot/advanced-run`, { period, steps, mergeOptions });
    },

    /**
     * Run a LightSpeed R advanced manual flight with only the specified steps.
     *
     * @param {String|Number} clientId
     * @param {String} period - yyyymm format (e.g. "202604")
     * @param {Array<string>} steps - subset of: products, inventory, orders, transfers, purchase_orders
     * @param {Object} mergeOptions - optional keys: actuals, includeHardMarkdowns, onOrder, receiving
     * @returns {Promise}
     */
    async runLightSpeedRAdvanced(clientId, period, steps, mergeOptions = {}) {
        return Request.post(`/api/clients/${clientId}/lightspeed-r/advanced-run`, { period, steps, mergeOptions });
    },

    /**
     * Run merge-only operations for LightSpeed R over a date range.
     *
     * @param {String|Number} clientId
     * @param {Object} mergeOptions - optional keys: actuals, includeHardMarkdowns, onOrder, receiving
     * @param {{startDate?: String, endDate?: String}} range optional date range payload
     * @returns {Promise}
     */
    async mergeLightSpeedR(clientId, mergeOptions = {}, range = {}) {
        const body = { mergeOptions };
        if (range?.startDate) body.startDate = range.startDate;
        if (range?.endDate) body.endDate = range.endDate;
        return Request.post(`/api/clients/${clientId}/lightspeed-r/merge`, body);
    },

    /**
     * Get advanced settings for a LightSpeed R client.
     * Returns auto_merge_actuals, api_refresh_token, and api_key.
     *
     * @param {String|Number} clientId
     * @returns {Promise}
     */
    async getLightSpeedRAdvancedSettings(clientId) {
        return Request.get(`/api/clients/${clientId}/lightspeed-r/advanced-settings`);
    },

    /**
     * Update advanced settings for a LightSpeed R client.
     *
     * @param {String|Number} clientId
     * @param {Object} settings - { auto_merge_actuals, api_refresh_token, api_key }
     * @returns {Promise}
     */
    async updateLightSpeedRAdvancedSettings(clientId, settings) {
        return Request.post(`/api/clients/${clientId}/lightspeed-r/advanced-settings`, settings);
    },

    /**
     * Get advanced settings for a Shopify Pilot client.
     * Returns shopify_domain, auto_merge_actuals, include_hard_markdowns, purchase_order_system, and (for stocky) store_name/api_key.
     *
     * @param {String|Number} clientId
     * @returns {Promise}
     */
    async getShopifyAdvancedSettings(clientId) {
        return Request.get(`/api/clients/${clientId}/shopify/advanced-settings`);
    },

    /**
     * Update advanced settings for a Shopify Pilot client.
     *
     * @param {String|Number} clientId
     * @param {Object} settings - { shopify_domain, auto_merge_actuals, include_hard_markdowns, purchase_order_system, store_name, api_key }
     * @returns {Promise}
     */
    async updateShopifyAdvancedSettings(clientId, settings) {
        return Request.post(`/api/clients/${clientId}/shopify/advanced-settings`, settings);
    },
}

export default FlightService